import { Box, Button, Card, Icon, Text } from '@factorialco/deprecated-design-system'

import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

interface EauStatusCardProps {
  status: string
  onRequestClick?: () => void
  disabled?: boolean
  updatedAt?: Date
  confirmedStartDate?: Date
  confirmedEndDate?: Date
  retryable?: boolean
}

const EauStatusCard = ({
  status,
  onRequestClick,
  disabled,
  updatedAt,
  confirmedStartDate,
  confirmedEndDate,
  retryable,
}: EauStatusCardProps) => {
  const formatUpdatedAt = (date?: Date) => {
    if (!date || isNaN(date.getTime())) return null
    const padZero = (num: number) => (num < 10 ? `0${num}` : `${num}`)
    return i18n.t('payroll_bundle.eau_status.updated_at', {
      time: `${padZero(date.getHours())}:${padZero(date.getMinutes())}`,
      date: i18nDateToString(date, 'ui_abbreviated_month_year')
    })
  }
  const formatDateRange = (startDate?: Date, endDate?: Date) => {
    if (!startDate || !endDate) return ''
    const startStr = startDate.toLocaleString('en-US', { day: 'numeric', month: 'short' })
    const endStr = endDate.toLocaleString('en-US', { day: 'numeric', month: 'short' })
    const year = endDate.getFullYear()
    return `${startStr}–${endStr}, ${year}`
  }
  const getCardConfig = () => {
    switch (status) {
      case 'request':
        return {
          background: 'grey200' as const,
          title: i18n.t('payroll_bundle.eau_status.request.title'),
          description: i18n.t('payroll_bundle.eau_status.request.description'),
          content: (
            <Button type="primary" onClick={onRequestClick} disabled={disabled || !onRequestClick}>
              {i18n.t('payroll_bundle.eau_status.request.button')}
            </Button>
          ),
          icon: null,
        }

      case 'in_progress':
        return {
          background: 'feedbackBackgroundInfo' as const,
          title: i18n.t('payroll_bundle.eau_status.in_progress.title'),
          description: i18n.t('payroll_bundle.eau_status.in_progress.description'),
          content: formatUpdatedAt(updatedAt),
          icon: <Icon.Medium icon="lightbulb" color="feedbackIconInfo" />,
        }

      case 'requested':
        return {
          background: 'feedbackBackgroundInfo' as const,
          title: i18n.t('payroll_bundle.eau_status.in_progress.title'),
          description: i18n.t('payroll_bundle.eau_status.in_progress.description'),
          content: formatUpdatedAt(updatedAt),
          icon: <Icon.Medium icon="lightbulb" color="feedbackIconInfo" />,
        }

      case 'failed_request':
        return {
          background: 'feedbackBackgroundCritical' as const,
          title: i18n.t('payroll_bundle.eau_status.failed_request.title'),
          description: i18n.t('payroll_bundle.eau_status.failed_request.description'),
          content: retryable && (
            <Button
              type="secondary"
              onClick={onRequestClick}
              disabled={disabled || !onRequestClick}
            >
              {i18n.t('payroll_bundle.eau_status.failed_request.retry_button')}
            </Button>
          ),
          icon: <Icon.Medium icon="alert-circle-line" color="feedbackIconCritical" />,
        }

      case 'confirmed': {
        const dateRangeText = formatDateRange(confirmedStartDate, confirmedEndDate)
        return {
          background: 'feedbackBackgroundPositive' as const,
          title: i18n.t('payroll_bundle.eau_status.confirmed.title'),
          description: dateRangeText
            ? i18n.t('payroll_bundle.eau_status.confirmed.description_with_dates', { dates: dateRangeText })
            : i18n.t('payroll_bundle.eau_status.confirmed.description'),
          content: formatUpdatedAt(updatedAt),
          icon: <Icon.Medium icon="thumbs-up" color="feedbackIconPositive" />,
        }
      }

      case 'dates_mismatch': {
        const mismatchDateText = formatDateRange(confirmedStartDate, confirmedEndDate)
        return {
          background: 'feedbackBackgroundWarning' as const,
          title: i18n.t('payroll_bundle.eau_status.dates_mismatch.title'),
          description: mismatchDateText
            ? i18n.t('payroll_bundle.eau_status.dates_mismatch.description_with_dates', { dates: mismatchDateText })
            : i18n.t('payroll_bundle.eau_status.dates_mismatch.description'),
          content: formatUpdatedAt(updatedAt),
          icon: <Icon.Medium icon="alert-sign" color="feedbackIconWarning" />,
        }
      }

      case 'incorrect_insurance_company':
        return {
          background: 'feedbackBackgroundCritical' as const,
          title: i18n.t('payroll_bundle.eau_status.incorrect_insurance_company.title'),
          description: i18n.t('payroll_bundle.eau_status.incorrect_insurance_company.description'),
          content: formatUpdatedAt(updatedAt),
          icon: <Icon.Medium icon="alert-circle-line" color="feedbackIconCritical" />,
        }

      case 'certificate_not_present':
        return {
          background: 'feedbackBackgroundCritical' as const,
          title: i18n.t('payroll_bundle.eau_status.certificate_not_present.title'),
          description: i18n.t('payroll_bundle.eau_status.certificate_not_present.description'),
          content: formatUpdatedAt(updatedAt),
          icon: <Icon.Medium icon="alert-circle-line" color="feedbackIconCritical" />,
        }

      case 'read_error':
        return {
          background: 'feedbackBackgroundCritical' as const,
          title: i18n.t('payroll_bundle.eau_status.read_error.title'),
          description: i18n.t('payroll_bundle.eau_status.read_error.description'),
          content: formatUpdatedAt(updatedAt),
          icon: <Icon.Medium icon="alert-circle-line" color="feedbackIconCritical" />,
        }

      default:
        return null
    }
  }

  const config = getCardConfig()

  // Don't render anything for unknown statuses
  if (!config) return null

  return (
    <Box background={config.background} borderRadius={{ all: 'abs016' }} padding="s24">
      <Box gap="s16">
        <Box flexDirection="row" alignItems="center" gap="s8">
          {config.icon}
          <Card.Title size="small">{config.title}</Card.Title>
        </Box>
        <Text.Normal color="black">
          {config.description}
        </Text.Normal>
        <Card.Content>
          <Text.Normal color="black">
            {config.content}
          </Text.Normal>
        </Card.Content>
      </Box>
    </Box>
  )
}

export default EauStatusCard
